import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { createSolicitud, getSolicitudes, deleteSolicitud } from "../../api/api";
import { ERROR_MESSAGES } from "../../utils/constants";
import { getMotivosAusencia } from "../../api/motivosAusencia";

/**
 * Componente de gestión de solicitudes para trabajadores.
 * Permite crear solicitudes de vacaciones/ausencias, ver el listado de solicitudes propias
 * y eliminar solicitudes pendientes. Incluye selección de motivos de ausencia.
 * 
 * @param {Object} props
 * @param {string} [props.viewMode='both'] - Modo de vista: 'form', 'list', o 'both'
 * @param {Function} [props.onClose] - Callback al cerrar el componente
 * @returns {JSX.Element} Formulario y lista de solicitudes
 */
export default function SolicitudesTrabajador({ viewMode = 'both', onClose = null }) {
  const { token, user } = useContext(AuthContext);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(viewMode === 'form');
  const [mensaje, setMensaje] = useState({ tipo: "", texto: "" });
  const [motivos, setMotivos] = useState([]);
  const [motivoSeleccionado, setMotivoSeleccionado] = useState("");
  const [motivoLibre, setMotivoLibre] = useState("");

  // Formulario
  const [formData, setFormData] = useState({
    tipo: "vacaciones",
    fechaInicio: "",
    fechaFin: "",
    horaInicio: "",
    horaFin: "",
    observaciones: "",
  });

  useEffect(() => {
    if (user?._id) {
      cargarSolicitudes();
      cargarMotivos();
    }
  }, [user?._id]);

  // Sync showForm when parent changes viewMode
  useEffect(() => {
    if (viewMode === 'form') setShowForm(true);
    else if (viewMode === 'list') setShowForm(false);
    // if 'both', keep current behavior (do not override)
  }, [viewMode]);

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      const data = await getSolicitudes(token);
      
      // Filtrar solo las solicitudes del usuario actual
      const misSolicitudes = data.filter(s => {
        const solicitudUserId = s.usuario?._id || s.usuario;
        return String(solicitudUserId) === String(user?._id);
      });
      
      setSolicitudes(misSolicitudes);
    } catch (error) {
      mostrarMensaje("error", "Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const cargarMotivos = async () => {
    try {
      const data = await getMotivosAusencia(token);
      setMotivos(data);
    } catch {
      setMotivos([]);
    }
  };

  const mostrarMensaje = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje({ tipo: "", texto: "" }), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!formData.fechaInicio || !formData.fechaFin) {
      mostrarMensaje("error", "Debes seleccionar las fechas");
      return;
    }

    if (new Date(formData.fechaInicio) > new Date(formData.fechaFin)) {
      mostrarMensaje("error", "La fecha de inicio no puede ser posterior a la fecha de fin");
      return;
    }

    if (formData.tipo === "ausencia" && (!formData.horaInicio || !formData.horaFin)) {
      mostrarMensaje("error", "Para ausencias debes indicar hora de inicio y fin");
      return;
    }
    // Validar que la hora de fin sea posterior a la de inicio
    if (formData.tipo === "ausencia" && formData.horaInicio && formData.horaFin) {
      const [hIni, mIni] = formData.horaInicio.split(":").map(Number);
      const [hFin, mFin] = formData.horaFin.split(":").map(Number);
      const inicio = hIni * 60 + mIni;
      const fin = hFin * 60 + mFin;
      if (fin <= inicio) {
        mostrarMensaje("error", "La hora de fin debe ser posterior a la hora de inicio");
        return;
      }
    }

    try {
      setLoading(true);
      let motivoAusencia = "";
      if (formData.tipo === "ausencia") {
        motivoAusencia = motivoSeleccionado === "otro" ? motivoLibre : motivoSeleccionado;
      }
      await createSolicitud(token, { ...formData, motivoAusencia });
      mostrarMensaje("exito", "Solicitud creada correctamente");
      setShowForm(false);
      setFormData({
        tipo: "vacaciones",
        fechaInicio: "",
        fechaFin: "",
        horaInicio: "",
        horaFin: "",
        observaciones: "",
      });
      setMotivoSeleccionado("");
      setMotivoLibre("");
      cargarSolicitudes();
    } catch (error) {
      const code = error.response?.data?.message;
      if (code && ERROR_MESSAGES[code]) {
        mostrarMensaje("error", ERROR_MESSAGES[code]);
      } else {
        mostrarMensaje("error", error.response?.data?.message || "Error al crear solicitud");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!confirm("¿Estás seguro de que quieres eliminar esta solicitud?")) return;

    try {
      setLoading(true);
      await deleteSolicitud(token, id);
      mostrarMensaje("exito", "Solicitud eliminada correctamente");
      cargarSolicitudes();
    } catch (error) {
      mostrarMensaje("error", error.response?.data?.message || "Error al eliminar solicitud");
    } finally {
      setLoading(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: "bg-yellow-100 text-yellow-800",
      aprobada: "bg-green-100 text-green-800",
      denegada: "bg-red-100 text-red-800",
    };
    return badges[estado] || "bg-gray-100 text-gray-800";
  };

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };


  return (
    <div className="bg-white rounded-xl shadow-lg p-3 sm:p-6 flex flex-col" style={{minHeight: '400px'}}>
          {/* Header removed (modal provides title) */}

      {/* Mensajes */}
      {mensaje.texto && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            mensaje.tipo === "exito" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
        >
          {/* Icono eliminado */}
          {mensaje.texto}
        </div>
      )}

        {/* Formulario (visible solo si viewMode !== 'list' and showForm true) */}
        {viewMode !== 'list' && showForm && (
        <div className="mb-6 flex-shrink-0">
          <form onSubmit={handleSubmit} className="p-3 sm:p-6 bg-gray-50 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Tipo */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Solicitud
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tipo"
                      value="vacaciones"
                      checked={formData.tipo === "vacaciones"}
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-gray-700">Vacaciones</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tipo"
                      value="ausencia"
                      checked={formData.tipo === "ausencia"}
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-gray-700">Ausencia</span>
                  </label>
                </div>
              </div>

              {/* Fecha Inicio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Inicio
                </label>
                <input
                  type="date"
                  value={formData.fechaInicio}
                  onChange={(e) => setFormData({ ...formData, fechaInicio: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1900-01-01"
                  max="2079-12-31"
                  required
                />
              </div>

              {/* Fecha Fin */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Fin
                </label>
                <input
                  type="date"
                  value={formData.fechaFin}
                  onChange={(e) => setFormData({ ...formData, fechaFin: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1900-01-01"
                  max="2079-12-31"
                  required
                />
              </div>

              {/* Horas y motivo (solo para ausencias) */}
              {formData.tipo === "ausencia" && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Hora de Inicio
                    </label>
                    <input
                      type="time"
                      value={formData.horaInicio}
                      onChange={(e) => setFormData({ ...formData, horaInicio: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required={formData.tipo === "ausencia"}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Hora de Fin
                    </label>
                    <input
                      type="time"
                      value={formData.horaFin}
                      onChange={(e) => setFormData({ ...formData, horaFin: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required={formData.tipo === "ausencia"}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Motivo de Ausencia
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2"
                      value={motivoSeleccionado}
                      onChange={e => setMotivoSeleccionado(e.target.value)}
                      required
                    >
                      <option value="">Selecciona un motivo</option>
                      {motivos.map(m => (
                        <option key={m._id} value={m.nombre}>{m.nombre}</option>
                      ))}
                      <option value="otro">Otro motivo...</option>
                    </select>
                    {motivoSeleccionado === "otro" && (
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="Escribe tu motivo"
                        value={motivoLibre}
                        onChange={e => setMotivoLibre(e.target.value)}
                        required
                      />
                    )}
                  </div>
                </>
              )}

              {/* Observaciones */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Observaciones (opcional)
                </label>
                <textarea
                  value={formData.observaciones}
                  onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="3"
                  placeholder="Añade cualquier información adicional..."
                ></textarea>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  if (typeof onClose === 'function') onClose();
                  else setShowForm(false);
                }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-center"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 text-center"
              >
                {loading ? "Creando..." : "Crear Solicitud"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Solicitudes: visible solo si viewMode !== 'form' */}
      {viewMode !== 'form' && (
        <div className="mt-2 flex-1 min-h-0" style={{overflowY: 'auto'}}>
        {loading && !showForm ? (
          <div className="text-center py-8">
            {/* Icono eliminado */}
            <p className="text-gray-500 mt-2">Cargando solicitudes...</p>
          </div>
        ) : solicitudes.length === 0 ? (
          <div className="text-center py-16">
            {/* Icono eliminado */}
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No hay solicitudes</h3>
            <p className="text-gray-500">Crea tu primera solicitud de vacaciones o ausencia</p>
          </div>
        ) : (
          <div className="space-y-4 pr-2">
            {solicitudes.map((sol) => (
              <div key={sol._id} className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition">
                {/* Fila superior: badge + tipo + botón eliminar */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${getEstadoBadge(sol.estado)}`}>
                      {sol.estado.toUpperCase()}
                    </span>
                    <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                      {sol.tipo === "vacaciones" ? "Vacaciones" : "Ausencia"}
                    </span>
                  </div>
                  {sol.estado === "pendiente" && (
                    <button
                      onClick={() => handleEliminar(sol._id)}
                      className="flex-shrink-0 text-xs font-medium text-red-600 hover:text-red-700 transition border border-red-200 rounded px-2 py-0.5"
                      title="Eliminar solicitud"
                    >
                      Eliminar
                    </button>
                  )}
                </div>
                {/* Fechas: una columna en móvil, dos en sm */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm">
                  <div className="flex flex-wrap items-baseline gap-x-1">
                    <span className="text-gray-500">Desde:</span>
                    <span className="font-semibold">{formatFecha(sol.fechaInicio)}</span>
                    {sol.tipo === "ausencia" && sol.horaInicio && (
                      <span className="text-gray-500">({sol.horaInicio})</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-1">
                    <span className="text-gray-500">Hasta:</span>
                    <span className="font-semibold">{formatFecha(sol.fechaFin)}</span>
                    {sol.tipo === "ausencia" && sol.horaFin && (
                      <span className="text-gray-500">({sol.horaFin})</span>
                    )}
                  </div>
                </div>
                {sol.observaciones && (
                  <p className="text-sm text-gray-600 mt-2 break-words">
                    {sol.observaciones}
                  </p>
                )}
                {sol.estado === "denegada" && sol.motivoRechazo && (
                  <div className="mt-2 p-2 bg-red-50 border-l-4 border-red-500 rounded">
                    <p className="text-sm text-red-800 break-words">
                      <strong>Motivo de rechazo:</strong> {sol.motivoRechazo}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </div>
      )}
    </div>
  );
}
